<script lang="ts" setup>
import type { PropType} from 'vue';
import { ref } from 'vue';

/**********
 * Init
 **********/
const emit = defineEmits(['change']);

const props = defineProps({
    value: {
        type: Boolean,
        default: false
    },
    checked: {
        type: Boolean,
        default: false
    },
    disabled: {
        type: Boolean,
        default: false
    },
    name: {
        type: String,
        default: ''
    },
    id: {
        type: [Object, String, Number],
        default: null
    },
    type: {
        type: String,
        default: 'checkbox'
    },
    changeData: {
        type: [String, Array, Object],
        default: null
    },
    size: {
        type: String,
        default: 'medium'
    },
    disableTab: {
        type: Boolean,
        default: false
    },
    position: {
        type: String as PropType<'left' | 'right'>,
        default: 'right'
    }
});

const hasFocus = ref<boolean>(false);

function onFocus(focus: boolean): void {
    hasFocus.value = focus;
}

</script>

<template>
    <label :for="props.id?.toString()" class="sy-checkbox-container flex-container sy-spacer-x-5">
        <slot v-if="position === 'left'"></slot>
        <input
            :id="id?.toString()"
            :tabindex="disableTab ? '-1' : ''"
            :type="type"
            :name="name"
            :value="value"
            :checked="checked"
            :disabled="disabled"
            @change="emit('change', $event, changeData)"
            @focus="onFocus(true)"
            @blur="onFocus(false)"
        />
        <span :class="['sy-checkbox', {normalSize : size == 'medium', smallSize : size == 'small' }, { focused: hasFocus } ] "></span>
        <slot v-if="position === 'right'"></slot>
    </label>
</template>

<style scoped lang="scss">
.sy-checkbox-container {
    cursor: pointer;
    align-items: center;
    line-height: 1.25rem;

    // Hide the default checkbox
    input {
        width: 0;
        height: 0;
        opacity: 0;
        left: -50rem;
        position: absolute;

        // Show the checkmark when checked
        &:checked ~ .sy-checkbox {
            border: none;
            background-color: var(--sy-blue-600);

            &:after {
                display: block;
            }
        }
    }

    &:hover {
        input ~ .sy-checkbox {
            &:after {
                display: block;
                opacity: .2;
            }
        }

        input:checked ~ .sy-checkbox {
            &:after {
                display: block;
                opacity: 1;
            }
        }

        // Reset on disabled
        input:disabled ~ .sy-checkbox {
            &:after {
                display: none;
            }
        }

        input:disabled:checked ~ .sy-checkbox:after {
            display: block;
            opacity: 1;
        }
    }

    // Custom checkbox
    .sy-checkbox {
        border: 1px solid var(--sy-gray-400);
        border-radius: var(--sy-border-radius);
        position: relative;

        &.focused {
            border-color: var(--sy-gray-700);
        }

        // Checkmark
        &:after {
            content: "";
            position: absolute;
            display: none;
            left: 40%;
            top: 0.188rem;
            border: solid var(--sy-white);
            border-width: 0 1px 1px 0;
            -webkit-transform: rotate(45deg);
            -ms-transform: rotate(45deg);
            transform: rotate(45deg);
        }
    }

    .normalSize {
        height: 1.25rem;
        width: 1.25rem;

        // Checkmark
        &:after {
            width: .3rem;
            height: .7rem;
        }
    }

    .smallSize {
        height: 1rem;
        width: 1rem;

        // Checkmark
        &:after {
            width: .25rem;
            height: .5rem;
        }
    }

    input[disabled] ~ .sy-checkbox {
        background: rgba(var(--sy-gray-400-rgb), .2);
        border-color: rgba(var(--sy-gray-900-rgb), .2);
        cursor: not-allowed;

        &:after {
            border-color: rgba(var(--sy-gray-900-rgb), .2);
        }
    }
}
</style>
